/**
 * ItemsDisplayWrapper - Wrapper component that displays media and text items in two columns
 * - Left column: Media items using MediaItemsGallery
 * - Right column: Text items using TextItemsList
 * - Responsive: Stacks vertically on mobile
 */

import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MediaItemsGallery from "./MediaItemsGallery";
import TextItemsList from "./TextItemsList";
import { logDebug } from "../../shared/utils/logger.js";

export default function ItemsDisplayWrapper({
  items = [],
  entityId,
  entityType = "quotes", // "quotes" | "leads"
  maxItems = 20,
  showSectionHeaders = true,
  compact = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Separate items by type
  const { mediaItems, textItems } = useMemo(() => {
    logDebug("ItemsDisplayWrapper: received items", {
      total: items.length,
    });
    const media = items.filter(
      (item) =>
        item.type === "image" ||
        item.type === "video" ||
        item.type === "document" ||
        item.type === "audio" ||
        item.type === "spreadsheet" ||
        item.type === "csv"
    );
    const text = items.filter((item) => item.type === "text");
    logDebug("ItemsDisplayWrapper: categorized items", {
      mediaCount: media.length,
      textCount: text.length,
    });
    return { mediaItems: media, textItems: text };
  }, [items]);

  if (!items || items.length === 0) {
    return (
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: "italic" }}
        >
          No items available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {showSectionHeaders && (
        <Typography variant="h6" gutterBottom>
          Items ({items.length})
        </Typography>
      )}

      <Grid container spacing={2}>
        {/* Media Items Column - Only render if media items exist */}
        {mediaItems.length > 0 && (
          <Grid item xs={12} md={textItems.length > 0 ? 6 : 12}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent sx={{ p: compact ? 2 : 3 }}>
                <MediaItemsGallery
                  items={mediaItems}
                  entityId={entityId}
                  entityType={entityType}
                  maxItems={maxItems}
                  miniCardWidth={compact ? 100 : 120}
                  miniCardHeight={compact ? 120 : 140}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Text Items Column - Only render if text items exist */}
        {textItems.length > 0 && (
          <Grid item xs={12} md={mediaItems.length > 0 ? 6 : 12}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent sx={{ p: compact ? 2 : 3 }}>
                <TextItemsList
                  items={textItems}
                  maxItems={maxItems}
                  showTimestamps={true}
                  compact={compact}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
